import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { JSONContent } from '@tiptap/react';
import { motion } from 'framer-motion';
import { Poll, Send } from '@/components/icons';
import { COMMUNITY_TOPICS, POST_TITLE_MAX, POST_TITLE_MIN, type CommunityTopicKey, type PostDetailDto, type SurveyInput } from '@zoxo/shared';

import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useSnackbar } from '../../components/ui/magic-snackbar';
import { ApiError } from '../../lib/api';
import { useLeaveGuard } from '../../lib/use-leave-guard';
import { cn } from '../../lib/utils';
import { RichEditor, ToolbarButton } from '../articles/editor/rich-editor';
import { EMPTY_SURVEY, SurveyBuilder, surveyReady } from './survey-builder';
import { useCreatePost, useUpdatePost } from './use-posts';

/**
 * 커뮤니티 글쓰기·수정. **아티클 작성 폼과 같은 UI** — 머리줄(취소·등록), 카드(제목 → 본문 → 게시판),
 * 작성 팁. 승인 게이트가 없다 — 등록하면 바로 보인다.
 */
export function PostEditor({ post, initialTopic }: { post?: PostDetailDto; initialTopic?: CommunityTopicKey }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const create = useCreatePost();
  const update = useUpdatePost(post?.id ?? '');
  const [topic, setTopic] = useState<CommunityTopicKey | null>(post?.topic ?? initialTopic ?? null);
  const [title, setTitle] = useState(post?.title ?? '');
  const [body, setBody] = useState<JSONContent | undefined>(post ? (post.body as JSONContent) : undefined);
  // 수정 화면은 붙어 있던 설문을 그대로 연다. 투표가 이미 있으면 서버가 교체를 409 로 막는다.
  // 떼는 것(null)은 된다. 안 바꿨으면 보내지 않는다 — 보내면 서버가 지우고 다시 넣는다.
  const initialSurvey: SurveyInput | null =
    post?.survey ? { answerType: post.survey.answerType, options: post.survey.options.map((o) => ({ label: o.label })) } : null;
  const [survey, setSurvey] = useState<SurveyInput | null>(initialSurvey);
  const surveyChanged = JSON.stringify(survey) !== JSON.stringify(initialSurvey);
  // 본문은 편집기가 손댈 때만 새 객체로 바뀐다(onUpdate) — 참조 비교로 충분하다.
  const initialBody = post ? (post.body as JSONContent) : undefined;
  const dirty =
    title !== (post?.title ?? '') || body !== initialBody || surveyChanged || (post !== undefined && topic !== post.topic);
  const allowLeave = useLeaveGuard(dirty);

  const t = title.trim();
  /*
    게시판이 비어도 버튼은 산다 — 눌러야 「게시판을 골라야 한다」 를 빨간 글자로 말할 수 있다.
    잠긴 버튼은 왜 잠겼는지 말하지 않는다 (2026-09-26 형).
  */
  const [topicMissing, setTopicMissing] = useState(false);
  const contentReady = t.length >= POST_TITLE_MIN && t.length <= POST_TITLE_MAX && !!body && surveyReady(survey);
  const canSave = topic !== null && contentReady;
  const pending = create.isPending || update.isPending;
  const failure = create.error ?? update.error;
  const errorText = failure
    ? failure instanceof ApiError && failure.status === 409
      ? '이미 투표가 있는 설문은 바꿀 수 없다. 설문을 빼거나 그대로 두자.'
      : '저장하지 못했다. 잠시 후 다시 시도하자.'
    : null;

  const save = () => {
    if (topic === null) {
      setTopicMissing(true);
      document.getElementById('post-topic')?.focus();
      return;
    }
    if (!canSave || !body) return;
    if (post) {
      update.mutate({ topic, title: t, body, ...(surveyChanged ? { survey } : {}) }, {
        onSuccess: () => { allowLeave(); showSnackbar('글을 고쳤어요.', { type: 'success' }); void navigate({ to: '/posts/$id', params: { id: post.id } }); },
      });
    } else {
      create.mutate({ topic, title: t, body, survey }, {
        onSuccess: (created) => { allowLeave(); showSnackbar('글을 올렸어요.', { type: 'success' }); void navigate({ to: '/posts/$id', params: { id: created.id } }); },
      });
    }
  };

  return (
    // 아티클 작성 폼(articles/editor/article-editor.tsx)과 같은 뼈대 — 머리줄, 카드, 작성 팁, 화면 채우기.
    <div className="flex min-h-full flex-col pb-24">
      {/* Top bar */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-text">{post ? '글 수정' : '글쓰기'}</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            disabled={pending}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-text-muted transition-colors hover:bg-surface-muted disabled:opacity-50"
          >
            취소
          </button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={save}
            disabled={pending || !contentReady}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
              !contentReady
                ? 'border border-border bg-surface text-text-subtle'
                : 'bg-primary text-primary-foreground hover:bg-primary-hover',
            )}
          >
            <Send className="h-4 w-4" />
            {pending ? '저장 중…' : post ? '저장' : '등록'}
          </motion.button>
        </div>
      </div>

      {/* Error */}
      {errorText && (
        <div role="alert" className="mb-4 rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger">
          {errorText}
        </div>
      )}

      {/* Editor card */}
      <div className="flex flex-1 flex-col rounded-2xl bg-surface p-2.5 ring-1 ring-border sm:p-4">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={POST_TITLE_MAX}
          placeholder="제목 평어체로 작성"
          className="tiptap-serif mb-3 w-full border-none bg-transparent text-base leading-[1.75] text-text outline-none placeholder:text-text-subtle"
        />

        <div className="flex flex-1 flex-col border-t border-border pt-3">
          <RichEditor
            value={body}
            onChange={setBody}
            uploadKind="post"
            ariaLabel="글 본문"
            toolbarExtra={
              <ToolbarButton onClick={() => setSurvey((s) => s ?? EMPTY_SURVEY)} isActive={survey !== null} title="설문 추가">
                <Poll className="h-4 w-4" />
              </ToolbarButton>
            }
            paperClassName="-mx-2.5 sm:-mx-4"
            // 설문은 본문의 일부 — 읽기 화면과 같이 종이 안, 본문 아래.
            afterBody={survey ? <SurveyBuilder value={survey} onChange={setSurvey} onRemove={() => setSurvey(null)} /> : null}
          />
        </div>

        {/* 게시판 — 아티클의 태그 자리. 지원사업 목록과 같은 shadcn Select. */}
        <div className="mt-3 border-t border-border pt-3">
          <label htmlFor="post-topic" className="mb-2 block text-sm font-medium text-text">
            대상 게시판
          </label>
          <Select value={topic ?? undefined} onValueChange={(v) => { setTopic(v as CommunityTopicKey); setTopicMissing(false); }}>
            <SelectTrigger
              id="post-topic"
              aria-label="대상 게시판"
              aria-invalid={topicMissing || undefined}
              className={cn('w-[220px]', topicMissing && 'border-danger ring-1 ring-danger')}
            >
              <SelectValue placeholder="게시판 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>게시판</SelectLabel>
                {COMMUNITY_TOPICS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {topicMissing ? (
            <p role="alert" className="mt-2 text-sm text-danger">게시판을 골라야 한다.</p>
          ) : null}
        </div>
      </div>

      {/* Tips */}
      <div className="mt-4 rounded-xl bg-surface px-4 py-3 text-[13px] text-text-muted ring-1 ring-border">
        <p className="font-medium text-text">작성 팁</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>사실 위주로 평어체를 사용하자. 어줍잖은 경어체는 이곳에서 만들려는 분위기가 아니다.</li>
          <li>사람·회사 실명은 명예훼손의 위험이 있으므로 밝히지 말자.</li>
          <li>글은 필터링없이 바로 등록된다. 비방·광고성 글을 올리는 경우, 사라질 수 있다.</li>
        </ul>
      </div>
    </div>
  );
}
